import { createSelector } from "@reduxjs/toolkit";

import { getServiceIcon } from "Components/ServiceIcon/helper";
import { getResourceProfile } from "Constants/constants";
import { isDifferent } from "Libs/difference";
import { setDeep } from "Libs/objectAccess";
import { objectEntries, sortAlphabetically } from "Libs/utils";
import { gitProjectSelector } from "Reducers/project/project";
import { projectSettingsSizingApiSelector } from "Reducers/projectSettings";
import {
  getDeploymentWebApps,
  getDeploymentServices
} from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";

import {
  CURRENT_DEPLOYMENT_ID,
  NEXT_DEPLOYMENT_ID,
  BLACKFIRE_ID_VARIABLE
} from "./constants";
import {
  retrieveOneResourceRawDataHandler,
  resourcesAccumulatorHandler,
  resourcesFormatHandler,
  deploymentResourcesHandler,
  transformServices
} from "./util";

import type {
  ServiceDecodedParams,
  AppAndServiceProps,
  ResourceCollectionType
} from "./types";
import type { AppRelationshipsTransformedType } from "../service";
import type { DeploymentService } from "@packages/client";
import type { RootState } from "Store/configureStore";

type BasicDeploymentSelectorParams = {
  organizationId?: string;
  projectId?: string;
  environmentId?: string;
};

export const deploymentStateSelector = (state: RootState) => state.deployment;

export const deploymentDataSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.data
);

export const selectDeployment = createSelector(
  deploymentDataSelector,
  gitProjectSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentData, project, { organizationId, projectId, environmentId }) => {
    const envId = environmentId ?? project?.default_branch;
    if (!organizationId || !projectId || !envId) {
      return;
    }

    return deploymentData?.[organizationId]?.[projectId]?.[envId];
  }
);

export const currentDeploymentSelector = createSelector(
  selectDeployment,
  deployment => deployment?.[CURRENT_DEPLOYMENT_ID]
);

export const nextDeploymentSelector = createSelector(
  selectDeployment,
  deployment => deployment?.[NEXT_DEPLOYMENT_ID]
);

export const deploymentHasLanguageSelector = createSelector(
  currentDeploymentSelector,
  (_: RootState, params: { languages: string[] }) => params,
  (currentDeployment, { languages = [] }) => {
    const webapps = currentDeployment?.webapps || {};

    return Object.keys(webapps || {}).some(appName =>
      languages.some(supportType => {
        const appType =
          currentDeployment?.webapps[appName].type.toLowerCase?.();
        return appType?.indexOf(supportType.toLowerCase?.()) !== -1;
      })
    );
  }
);

export const transformedDeploymentServicesSelector = createSelector(
  currentDeploymentSelector,
  deployment => {
    if (!deployment) {
      return;
    }

    const webapps = transformServices(deployment.webapps, "webapp");
    const workers = transformServices(deployment.workers, "worker");
    const services = transformServices(deployment.services, "service");

    return Object.fromEntries(
      [...webapps, ...services, ...workers].map(service => [
        service.name,
        service
      ])
    );
  }
);

export const currentMetricsServicesSelector = createSelector(
  currentDeploymentSelector,
  currentDeployment => {
    if (!currentDeployment) {
      return {};
    }

    const webapps = currentDeployment.webapps;
    const workers = currentDeployment.workers;
    const services = Object.fromEntries(
      sortAlphabetically(
        Object.entries(currentDeployment.services),
        ([key]) => key
      )
    );

    return Object.fromEntries(
      Object.entries({
        ...webapps,
        ...services,
        ...workers
      }).map(
        ([key, value]: [string, DeploymentService & { icon?: string }]) => {
          const icon = value?.type.split(":")[0];
          value.icon = icon;
          value.name = key;
          return [key, value];
        }
      )
    );
  }
);

export const loadingNextDeploymentSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.loadingNext
);

export const loadingCurrentDeploymentSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.loadingCurrent
);

export const loadingDeploymentSettingsSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.loadingSettings
);

export const isLoadingSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState.loading
);

export const isUpdateLoadingSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState.updateLoading
);

export const deploymentBaseErrorSelector = createSelector(
  deploymentStateSelector,
  deployment => deployment?.errors
);

export const generalDeploymentErrorSelector = createSelector(
  deploymentBaseErrorSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentErrors, { organizationId, projectId, environmentId }) => {
    if (organizationId && projectId && environmentId) {
      return deploymentErrors?.[organizationId]?.[projectId]?.[environmentId];
    }
  }
);

export const currentDeploymentErrorSelector = createSelector(
  generalDeploymentErrorSelector,
  environmentErrorObj => environmentErrorObj?.[CURRENT_DEPLOYMENT_ID]
);

export const nextDeploymentErrorSelector = createSelector(
  generalDeploymentErrorSelector,
  environmentErrorObj => environmentErrorObj?.[NEXT_DEPLOYMENT_ID]
);
export const deploymentErrorSelector = createSelector(
  currentDeploymentErrorSelector,
  nextDeploymentErrorSelector,
  (currentError, nextError) => {
    if (currentError?.message) return currentError;
    if (nextError?.message) return nextError;
    return undefined;
  }
);

export const updateDeploymentSettingsErrorSelector = createSelector(
  generalDeploymentErrorSelector,
  environmentErrorObj => environmentErrorObj?.settings
);

export const selectDeploymentSettings = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { projectId, environmentId, organizationId }) => {
    if (organizationId && projectId && environmentId) {
      return deploymentState?.settings?.[organizationId]?.[projectId]?.[
        environmentId
      ];
    }
  }
);

export const isExplicitDeploymentEnabledSelector = createSelector(
  selectDeploymentSettings,
  deploymentSettings => deploymentSettings?.explicit_deployments_enabled
);

export const responseCodesSelector = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { organizationId, projectId, environmentId }) => {
    if (organizationId && projectId && environmentId) {
      return deploymentState?.responseCode?.[organizationId]?.[projectId]?.[
        environmentId
      ];
    }
  }
);

export const deploymentStatusCodesSelector = createSelector(
  responseCodesSelector,
  responseCodes => {
    const { current, next } = responseCodes ?? {
      current: undefined,
      next: undefined
    };

    return { current, next };
  }
);

export const deploymentVariablesSelector = createSelector(
  deploymentDataSelector,
  gitProjectSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentData, project, { organizationId, projectId, environmentId }) => {
    const envId = environmentId ?? project?.default_branch;
    if (organizationId && projectId && envId) {
      return deploymentData?.[organizationId]?.[projectId]?.[envId]?.[
        CURRENT_DEPLOYMENT_ID
      ]?.variables;
    }
  }
);

export const blackfireEnabledInEnvironmentSelector = createSelector(
  deploymentVariablesSelector,
  variables =>
    variables?.some(variable => variable.name === BLACKFIRE_ID_VARIABLE)
);

export const blackfireUUIDSelector = createSelector(
  deploymentVariablesSelector,
  variables =>
    variables?.find(variable => variable.name === BLACKFIRE_ID_VARIABLE)?.value
);

export const allEnvironmentsResource = createSelector(
  deploymentDataSelector,
  (_: RootState, params: { organizationId: string; projectId: string }) =>
    params,
  (deploymentData, { organizationId, projectId }) => {
    const environmentLevel = deploymentData?.[organizationId]?.[projectId];
    const resources = Object.values(environmentLevel ?? {}).map(
      deploymentObj => deploymentObj?.[CURRENT_DEPLOYMENT_ID]
    );

    return resources?.filter(resource => typeof resource !== "undefined");
  }
);

export const deploymentServicesSelector = createSelector(
  deploymentStateSelector,
  deployment => deployment.services
);

export const currentDeploymentServicesSelector = createSelector(
  deploymentServicesSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (currentDeploymentServices, { organizationId, projectId, environmentId }) => {
    if (organizationId && projectId && environmentId) {
      return currentDeploymentServices?.[organizationId]?.[projectId]?.[
        environmentId
      ];
    }
  }
);

export const selectCurrentService = createSelector(
  currentDeploymentServicesSelector,
  (_: RootState, params: ServiceDecodedParams) => params,
  (
    currentDeploymentData,
    { appName }
  ):
    | {
        class: string;
        icon?: string;
        name: string;
        type: string;
        relationships: Record<string, AppRelationshipsTransformedType>;
      }
    | undefined => {
    const dataService =
      currentDeploymentData?.[appName] ??
      currentDeploymentData?.["webapps"]?.[appName] ??
      currentDeploymentData?.["services"]?.[appName];

    return dataService;
  }
);

export const selectCurrentServiceWorkers = createSelector(
  currentDeploymentServicesSelector,
  (_: RootState, params: ServiceDecodedParams) => params,
  (currentDeploymentData, { appName }) => {
    const workers = currentDeploymentData?.["workers"] || [];

    const serviceWorkers = Object.values(workers).filter(worker =>
      worker?.name.startsWith(`${appName}--`)
    );

    return serviceWorkers;
  }
);

export const selectCurrentServiceCronJobs = createSelector(
  selectCurrentService,
  appNameService => {
    const crons = appNameService?.["crons"] ?? {};

    const cronsEntries = Object.entries(crons);
    if (!cronsEntries.length) return [];

    const serviceCronJobs = cronsEntries.map(([key, value]) => ({
      ...(value as object),
      name: key
    }));

    return serviceCronJobs;
  }
);

export const currentAppAndServiceSelector = createSelector(
  currentDeploymentSelector,
  (_: RootState, params: AppAndServiceProps) => params,
  (currentDeployment, { appName, key }) =>
    appName ? currentDeployment?.[key]?.[appName] : undefined
);

export const currentAppSelector = createSelector(
  currentAppAndServiceSelector,
  (_: RootState, params: AppAndServiceProps) => params,
  (app, { appName, key }) => {
    if (key === "webapps" && appName) {
      const { iconName } = getServiceIcon(app as DeploymentService);

      const a = getDeploymentWebApps({
        name: appName,
        iconName,
        currentLine: 0,
        column: 0,
        app: app as DeploymentService
      });

      return a;
    }
  }
);

export const currentServiceSelector = createSelector(
  currentAppAndServiceSelector,
  (_: RootState, params: AppAndServiceProps) => params,
  (service, { appName: serviceName, key }) => {
    if (key === "services" && serviceName) {
      const type = service?.type?.split(":")?.[1];

      const s = getDeploymentServices({
        name: serviceName,
        type,
        currentLine: 0,
        column: 0,
        service: service as DeploymentService
      });

      return s;
    }
  }
);

export const nonCalculatedOneDeploymentResourceSelector = createSelector(
  currentDeploymentSelector,
  (
    _: RootState,
    params: {
      resourceType: ResourceCollectionType;
      appName: string;
    }
  ) => params,
  (currentDeployment, { resourceType, appName }) => {
    const {
      cpu,
      ram,
      instances,
      disk,
      status,
      cronsQuantity,
      containerProfile
    } = retrieveOneResourceRawDataHandler({
      currentDeployment,
      resourceType,
      appName
    });

    const currentResourceFormatted = resourcesFormatHandler({
      cpu,
      ram,
      instances,
      disk,
      status
    });

    return {
      ...currentResourceFormatted,
      profileLabel: getResourceProfile(containerProfile ?? ""),
      crons: cronsQuantity
    };
  }
);

export const oneDeploymentResourceSelector = createSelector(
  currentDeploymentSelector,
  (
    _: RootState,
    params: {
      resourceType: ResourceCollectionType;
      appName: string;
    }
  ) => params,
  (currentDeployment, { resourceType, appName }) => {
    const currentResource = {
      cpu: 0,
      ram: 0,
      instances: 0,
      disk: 0,
      status: ""
    };
    const {
      cpu,
      ram,
      instances,
      disk,
      status,
      cronsQuantity,
      containerProfile
    } = retrieveOneResourceRawDataHandler({
      currentDeployment,
      resourceType,
      appName
    });

    const currentResourceFormatted = resourcesFormatHandler(
      resourcesAccumulatorHandler(currentResource, {
        cpu,
        ram,
        instances,
        disk,
        status
      })
    );

    return {
      ...currentResourceFormatted,
      profileLabel: getResourceProfile(containerProfile ?? ""),
      crons: cronsQuantity
    };
  }
);

export const deploymentResourcesSelector = createSelector(
  currentDeploymentSelector,
  currentDeployment => deploymentResourcesHandler(currentDeployment)
);

export const deploymentSizingApiEnabledSelector = createSelector(
  currentDeploymentSelector,
  nextDeploymentSelector,
  projectSettingsSizingApiSelector,
  (currentDeployment, nextDeployment, projectSettingSizingApi) => {
    const deployment = currentDeployment ?? nextDeployment;
    const deploymentSizingApi =
      deployment?.project_info?.settings?.sizing_api_enabled;

    if (typeof deploymentSizingApi === "undefined") {
      return projectSettingSizingApi;
    }
    return deploymentSizingApi;
  }
);

export const deploymentValueChecksSelector = createSelector(
  currentDeploymentSelector,
  nextDeploymentSelector,
  (currentDeployment, nextDeployment) => {
    const nextValues: Record<string, any> = {};
    const currentValues: Record<string, any> = {};

    const deploymentNext = {
      webapps: nextDeployment?.webapps,
      workers: nextDeployment?.workers,
      services: nextDeployment?.services
    };

    const deploymentCurrent = {
      webapps: currentDeployment?.webapps,
      workers: currentDeployment?.workers,
      services: currentDeployment?.services
    };

    [deploymentNext, deploymentCurrent].forEach((deployment, i) => {
      const deploymentGroup = i === 0 ? nextValues : currentValues;
      objectEntries(deployment).forEach(
        ([resourceCollectionType, resourceCollection]) => {
          objectEntries(resourceCollection ?? {}).forEach(([label, data]) => {
            setDeep(
              deploymentGroup,
              [resourceCollectionType, label, "instance_count"],
              data?.instance_count
            );

            setDeep(
              deploymentGroup,
              [resourceCollectionType, label, "resources", "profile_size"],
              data?.resources.profile_size
            );

            setDeep(
              deploymentGroup,
              [resourceCollectionType, label, "disk"],
              data?.disk
            );
          });
        }
      );
    });

    return { nextValues, currentValues };
  }
);

export const resourcesDeploymentStatusSelector = createSelector(
  deploymentStatusCodesSelector,
  deploymentValueChecksSelector,
  ({ current: currentCode, next: nextCode }, { nextValues, currentValues }) => {
    if (nextCode === 400 && currentCode === 404) {
      return "Bad configuration";
    } else if (
      (nextCode === 200 &&
        currentCode === 200 &&
        isDifferent(nextValues, currentValues)) ||
      (nextCode === 200 && currentCode === 404)
    ) {
      return "No resources configured";
    } else if (nextCode?.toString().match(/^[45]/) && currentCode === 200) {
      return "Build failed";
    }
    return "";
  }
);

export const allResourcesHaveProfileSizeSelector = createSelector(
  nextDeploymentSelector,
  nextDeployment => {
    if (typeof nextDeployment === "undefined") {
      return;
    }
    let hasProfileSize = true;
    const resources = {
      webapps: nextDeployment?.webapps ?? {},
      workers: nextDeployment?.workers ?? {},
      services: nextDeployment?.services ?? {}
    };

    Object.values(resources).forEach(resourceCollection => {
      Object.values(resourceCollection).forEach(data => {
        hasProfileSize = hasProfileSize && !!data?.resources?.profile_size;
      });
    });

    return hasProfileSize;
  }
);
